import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Layers, 
  Briefcase, 
  Calendar,
  AlertCircle,
  TrendingUp,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [myAssignments, setMyAssignments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, assignmentsRes, sectorsRes, functionsRes] = await Promise.all([
        fetch(`${API_URL}/api/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/assignments/my`, { credentials: 'include' }),
        fetch(`${API_URL}/api/sectors`, { credentials: 'include' }),
        fetch(`${API_URL}/api/functions`, { credentials: 'include' }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (assignmentsRes.ok) setMyAssignments(await assignmentsRes.json());
      if (sectorsRes.ok) setSectors(await sectorsRes.json());
      if (functionsRes.ok) setFunctions(await functionsRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectorName = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    return sector?.name || 'N/A';
  };

  const getFunctionName = (functionId) => {
    const func = functions.find(f => f.function_id === functionId);
    return func?.name || 'N/A';
  };

  const getSectorColor = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    const colorMap = {
      orange: 'text-orange-500 bg-orange-500/10 border-orange-500/20',
      cyan: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20',
      emerald: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
      amber: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
    };
    return colorMap[sector?.color] || 'text-zinc-400 bg-zinc-800';
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      'Fixo': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      'Quinzenal': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'Mensal': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'Eventual': 'bg-zinc-700 text-zinc-300',
      'Avaliando': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    };
    return colors[frequency] || 'bg-zinc-700 text-zinc-300';
  };

  // Sort assignments by date (upcoming first)
  const sortedAssignments = [...myAssignments].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );

  const upcomingAssignments = sortedAssignments.filter(
    a => new Date(a.date) >= new Date(new Date().toDateString())
  );

  const pastAssignments = sortedAssignments.filter(
    a => new Date(a.date) < new Date(new Date().toDateString())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight">
            Olá, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-zinc-400 mt-1">
            Bem-vindo ao Kitchen Scale Manager
          </p>
        </div>

        {/* User Info Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center text-2xl font-semibold text-zinc-300">
                  {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-zinc-100">{user?.name}</h2>
                  <p className="text-zinc-500">{user?.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Badge className={`${getFrequencyBadge(user?.frequency)} border`}>
                  <Clock className="w-3 h-3 mr-1" />
                  {user?.frequency || 'Eventual'}
                </Badge>
                <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                  {user?.role}
                </Badge>
                {user?.dc50 && (
                  <Badge className="bg-green-500/10 text-green-400 border border-green-500/20">
                    DC-50
                  </Badge>
                )}
                {user?.training && (
                  <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    Treinado
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid (Admin only) */}
        {isAdmin && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-zinc-800">
                    <Users className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-100">{stats.total_users}</p>
                    <p className="text-xs text-zinc-500">Usuários</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-zinc-800">
                    <Layers className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-100">{stats.total_sectors}</p>
                    <p className="text-xs text-zinc-500">Setores</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-zinc-800">
                    <Briefcase className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-100">{stats.total_functions}</p>
                    <p className="text-xs text-zinc-500">Funções</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-zinc-800">
                    <Calendar className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-100">{stats.total_schedules}</p>
                    <p className="text-xs text-zinc-500">Escalas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-zinc-800">
                    <TrendingUp className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-100">{stats.total_assignments}</p>
                    <p className="text-xs text-zinc-500">Atribuições</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">{stats.open_slots}</p>
                    <p className="text-xs text-zinc-500">Vagas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assignments Section */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Upcoming Assignments */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-zinc-400" />
                Próximas Designações
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingAssignments.length === 0 ? (
                <p className="text-zinc-500 text-sm">Nenhuma designação futura</p>
              ) : (
                <div className="space-y-3">
                  {upcomingAssignments.slice(0, 5).map((assignment) => {
                    // Fix timezone issue: parse date as local time
                    const [year, month, day] = assignment.date.split('-').map(Number);
                    const dateObj = new Date(year, month - 1, day);
                    return (
                      <div 
                        key={assignment.assignment_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-800"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-zinc-200">
                            {dateObj.toLocaleDateString('pt-BR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            })}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {getFunctionName(assignment.function_id)}
                          </p>
                        </div>
                        <Badge className={`${getSectorColor(assignment.sector_id)} border`}>
                          {getSectorName(assignment.sector_id)}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* History */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                <Clock className="w-5 h-5 text-zinc-400" />
                Histórico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pastAssignments.length === 0 ? (
                <p className="text-zinc-500 text-sm">Nenhuma designação anterior</p>
              ) : (
                <div className="space-y-3">
                  {pastAssignments.slice(0, 5).map((assignment) => {
                    // Fix timezone issue: parse date as local time
                    const [year, month, day] = assignment.date.split('-').map(Number);
                    const dateObj = new Date(year, month - 1, day);
                    return (
                      <div 
                        key={assignment.assignment_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800/50 opacity-75"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-zinc-300">
                            {dateObj.toLocaleDateString('pt-BR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            })}
                          </p>
                          <p className="text-xs text-zinc-600">
                            {getFunctionName(assignment.function_id)}
                          </p>
                        </div>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-500">
                          {getSectorName(assignment.sector_id)}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
